import { FC, useState, useRef } from 'react';
import React from 'react';
import { aiApi } from '../../lib/apiClient';
import { toast } from '../../lib/toast';
import styles from './AiGenerator.module.css';
import { AiGenerationTypes } from '../../enums/aiGenerationTypes';
import { CheckCircle, XCircle, RefreshCw, Sparkles } from 'lucide-react';
import useOutsideClick from '../../hooks/useOutsideClick';

interface AiGeneratorProps {
    prompt: string;
    setContent: (val: string) => void;
    loading: boolean;
    setAiLoading: (val: boolean) => void;
    icon?: React.ReactNode;
    buttonText?: string;
    disabled?: boolean;
    previousData?: string;
    generateBody: { type: string; description: string; context?: any };
}

const AiGenerator: FC<AiGeneratorProps> = ({
    prompt,
    setContent,
    loading,
    icon,
    buttonText,
    setAiLoading,
    disabled = false,
    previousData,
    generateBody
}) => {
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [previousPrompt, setPreviousPrompt] = useState('');
    const [loadingButton, setLoadingButton] = useState<string | null>(null);
    const rollbackRef = useRef<HTMLDivElement>(null);

    useOutsideClick(rollbackRef, () => setIsSubmitted(false), isSubmitted);

    const handleGenerate = async () => {
        setLoadingButton('generate');
        setAiLoading(true);
        try {
            setPreviousPrompt(previousData ?? prompt);
            const response: any = await aiApi.generateContent(generateBody);
            let text: string;
            if (generateBody.type === AiGenerationTypes.generalSchema) {
                const raw = response?.content !== undefined ? response.content : response;
                text = typeof raw === 'string' ? raw : JSON.stringify(raw, null, 2);
            } else {
                text = typeof response === 'string' ? response : JSON.stringify(response, null, 2);
            }
            setContent((text ?? '').toString().trim());
            setIsSubmitted(true);
            toast.success('AI generated successfully!');
        } catch (error: any) {
            toast.error(`Failed to generate body template: ${error.message}`);
        } finally {
            setAiLoading(false);
            setLoadingButton(null);
        }
    };

    const handleRollback = () => {
        setLoadingButton('rollback');
        setContent(previousPrompt);
        setPreviousPrompt('');
        setIsSubmitted(false);
        setLoadingButton(null);
    };

    const handleRetry = async () => {
        setLoadingButton('retry');
        setIsSubmitted(false);
        await handleGenerate();
    };

    return (
        <div className={styles.container}>
            {isSubmitted && (
                <div className={styles.rollbackContainer} ref={rollbackRef}>
                    <button
                        onClick={() => setIsSubmitted(false)}
                        className={styles.keepButton}
                        disabled={loadingButton === 'keep'}
                    >
                        <CheckCircle
                            size={16}
                            className={`${styles.icon} ${loadingButton === 'keep' ? styles.loadingIcon : ''}`}
                        />
                        <span>Keep Changes</span>
                    </button>
                    <button
                        onClick={handleRollback}
                        className={styles.rollbackButton}
                        disabled={loadingButton === 'rollback'}
                    >
                        <XCircle
                            size={16}
                            className={`${styles.icon} ${loadingButton === 'rollback' ? styles.loadingIcon : ''}`}
                        />
                        <span>Rollback</span>
                    </button>
                    <button
                        onClick={handleRetry}
                        className={styles.retryButton}
                        disabled={loadingButton === 'retry'}
                    >
                        <RefreshCw
                            size={16}
                            className={`${styles.icon} ${loadingButton === 'retry' ? styles.loadingIcon : ''}`}
                        />
                        <span>Retry</span>
                    </button>
                </div>
            )}
            <button
                type="button"
                onClick={() => handleGenerate()}
                disabled={loadingButton === 'generate' || loading || !prompt?.trim() || disabled}
                className={styles.aiGenerateBtn}
            >
                {icon ? (
                    React.cloneElement(icon as React.ReactElement<{ className?: string }>, {
                        className: `${styles.icon} ${loadingButton === 'generate' ? styles.loadingIcon : ''}`
                    })
                ) : (
                    <Sparkles
                        size={16}
                        className={`${styles.icon} ${loadingButton === 'generate' ? styles.loadingIcon : ''}`}
                    />
                )}
                <span>{buttonText ? buttonText : loadingButton === 'generate' ? 'Generating...' : 'Generate with AI'}</span>
            </button>
        </div>
    );
};

export default AiGenerator;